import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, getStylesRef, Group} from '@mantine/core';
import {useMediaQuery} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/** Stagger between successive choice chips animating in, in milliseconds. */
const CHIP_STAGGER_MS = 50;
/** Duration of a single choice chip's entrance transition, in milliseconds. */
const CHIP_ANIM_DURATION_MS = 150;
/** Vertical offset a choice chip slides up from as it enters, in pixels. */
const CHIP_OFFSET_PX = 8;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Run a one-shot entrance animation that replays whenever `animationKey` changes.
 *
 * Returns whether the element should be in its "entered" (final) state. Uses a double
 * requestAnimationFrame so the browser paints the initial pre-transition state before flipping to
 * the entered state, guaranteeing the transition runs.
 * Honors `prefers-reduced-motion` by entering immediately.
 */
function useEntranceAnimation(animationKey: string): boolean {
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion) {
      setEntered(true);
      return;
    }
    setEntered(false);
    // Initialized to null so the cleanup can safely cancel it even if it runs before the first
    // requestAnimationFrame callback assigns the second frame.
    let secondFrame: number | null = null;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        setEntered(true);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame != null) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [animationKey, prefersReducedMotion]);

  return entered;
}

/** Border radius shared by every onboarding answer chip, in pixels (no matching theme token). */
export const CHIP_RADIUS_PX = 14;
/** Height shared by every onboarding answer chip, in pixels (no matching theme token). */
export const CHIP_HEIGHT_PX = 40;

const useChipStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  chip: {
    fontWeight: 400,
    height: CHIP_HEIGHT_PX,
    borderRadius: CHIP_RADIUS_PX,
    backgroundColor: selected ? theme.colors.blue[1] : theme.white,
    color: selected ? theme.colors.blue[8] : theme.colors.gray[9],
    border: `1px solid ${selected ? theme.colors.blue[1] : theme.colors.gray[2]}`,
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
      borderColor: theme.colors.blue[1],
      color: theme.colors.blue[8],
    },
    // The ghost variant paints aria-pressed/active chips grey; selected chips set aria-pressed, so
    // override it here to keep the blue selected look that mirrors the free-text "Something else" chip.
    '&[aria-pressed="true"]': {
      backgroundColor: theme.colors.blue[1],
      borderColor: theme.colors.blue[1],
      color: theme.colors.blue[8],
    },
  },
}));

/**
 * A single onboarding choice chip with a staggered entrance animation. Selection state is driven by
 * the parent; `aria-pressed` reflects it for assistive tech and tests. The chip cascades in (fade +
 * slide-up) delayed by `index`, replaying whenever `animationKey` (the question text) changes.
 */
export function AnimatedChoiceChip({
  label,
  selected,
  onSelect,
  index,
  animationKey,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  /** Position within the choices list; drives the stagger delay. */
  index: number;
  /** When this changes, the entrance animation replays (e.g. on a new question). */
  animationKey: string;
}): JSX.Element {
  const {classes} = useChipStyles({selected});
  const entered = useEntranceAnimation(animationKey);

  return (
    <Button
      aria-pressed={selected}
      variant="ghost"
      size="sm"
      className={classes.chip}
      onClick={onSelect}
      style={{
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0)' : `translateY(${CHIP_OFFSET_PX}px)`,
        transition: `opacity ${CHIP_ANIM_DURATION_MS}ms ease-out, transform ${CHIP_ANIM_DURATION_MS}ms ease-out`,
        transitionDelay: `${index * CHIP_STAGGER_MS}ms`,
      }}
    >
      {label}
    </Button>
  );
}

const useSkipStyles = createStyles((theme) => ({
  // A subtle "Skip →" control whose trailing arrow nudges right on hover.
  skip: {
    fontWeight: 600,
    color: theme.colors.gray[7],
    [`&:hover .${getStylesRef('arrow')}`]: {
      transform: 'translateX(3px)',
    },
  },
  arrow: {
    ref: getStylesRef('arrow'),
    transition: 'transform 150ms ease',
  },
}));

/**
 * The shared "Skip →" footer control. Used by every onboarding screen so the dismiss affordance is
 * identical across the agent survey, the interest/first-chat screen, and any fallbacks. The ✕ in the
 * shell remains the persistent exit; this advances/dismisses the current step.
 */
export function OnboardingSkipButton({
  onClick,
  disabled = false,
  testComponent,
}: {
  onClick: () => void;
  disabled?: boolean;
  /** Component name used for the test id. */
  testComponent: string;
}): JSX.Element {
  const {classes} = useSkipStyles();
  return (
    <Button
      variant="ghost"
      size="sm"
      radius="xl"
      className={classes.skip}
      onClick={onClick}
      disabled={disabled}
      rightIcon={<BenchlingIcon icon="arrow-right" aria-hidden={true} className={classes.arrow} />}
      {...getTestProps({component: testComponent, element: 'skip-button'})}
    >
      Skip
    </Button>
  );
}

const SEGMENT_HEIGHT_PX = 4;
const SEGMENT_WIDTH_PX = 20;
/**
 * Assumed number of onboarding steps. The backend doesn't tell us the total up front, so we render a
 * track of this many segments and fill them as the user progresses. If the survey turns out longer,
 * {@link OnboardingProgressBar} grows the track past this so it always holds at least `count` steps.
 */
const ASSUMED_TOTAL_STEPS = 10;

const useProgressStyles = createStyles((theme, {filled}: {filled: boolean}) => ({
  segment: {
    width: SEGMENT_WIDTH_PX,
    height: SEGMENT_HEIGHT_PX,
    borderRadius: SEGMENT_HEIGHT_PX / 2,
    // Filled = a step reached; unfilled = remaining track.
    backgroundColor: filled ? theme.colors.blue[6] : theme.colors.gray[2],
    transition: `background-color ${CHIP_ANIM_DURATION_MS}ms ease-out`,
  },
}));

/** A single bar segment, blue once its step is reached and a faint track until then. */
function ProgressSegment({filled}: {filled: boolean}): JSX.Element {
  const {classes} = useProgressStyles({filled});
  return <Box className={classes.segment} />;
}

/**
 * A stepped progress bar. We assume {@link ASSUMED_TOTAL_STEPS} steps and fill one segment per step
 * reached so far (including the question currently on screen), showing the rest as a faint track.
 * Because the real total isn't known up front, once `count` exceeds the assumed total we grow the
 * track to match — so discovering more questions simply adds (already-filled) steps rather than
 * overflowing a fixed "step N of M".
 */
export function OnboardingProgressBar({
  count,
  testComponent,
}: {
  count: number;
  /** Component name used for the test id. */
  testComponent?: string;
}): JSX.Element | null {
  if (count <= 0) {
    return null;
  }
  const total = Math.max(ASSUMED_TOTAL_STEPS, count);
  return (
    <Group
      spacing={6}
      {...(testComponent != null ? getTestProps({component: testComponent, element: 'progress'}) : {})}
    >
      {Array.from({length: total}, (_, i) => (
        <ProgressSegment key={i} filled={i < count} />
      ))}
    </Group>
  );
}
